"use client"

import { FlexProps, HStack } from "@chakra-ui/react"
import {
  AnimatePresence,
  domAnimation,
  LazyMotion,
  m,
  MotionProps,
  useInView,
  UseInViewOptions,
  Variants,
} from "motion/react"
import { FC, useEffect, useReducer, useRef } from "react"

type MarginType = UseInViewOptions["margin"]

interface BlurFadeProps extends FlexProps {
  children: React.ReactNode
  className?: string
  variant?: {
    hidden: { y: number }
    visible: { y: number }
  }
  duration?: number
  delay?: number
  offset?: number
  direction?: "up" | "down" | "left" | "right"
  inView?: boolean
  inViewMargin?: MarginType
  blur?: string
  motionProps?: MotionProps
}

interface PrintModeState {
  hasEnteredPrintMode: boolean
  isPrintMode: boolean
}

type PrintModeAction =
  | { type: "set-print-mode"; value: boolean }
  | { type: "enter-print-mode" }
  | { type: "leave-print-mode" }

const printModeReducer = (
  state: PrintModeState,
  action: PrintModeAction
): PrintModeState => {
  switch (action.type) {
    case "enter-print-mode":
      return { hasEnteredPrintMode: true, isPrintMode: true }
    case "leave-print-mode":
      return { ...state, isPrintMode: false }
    case "set-print-mode":
      return {
        hasEnteredPrintMode: state.hasEnteredPrintMode || action.value,
        isPrintMode: action.value,
      }
    default:
      return state
  }
}

const usePrintModeState = () => {
  const [printModeState, dispatchPrintMode] = useReducer(printModeReducer, {
    hasEnteredPrintMode: false,
    isPrintMode: false,
  })

  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }

    const printQuery = window.matchMedia("print")
    const updatePrintMode = () =>
      dispatchPrintMode({
        type: "set-print-mode",
        value: printQuery.matches,
      })
    const enablePrintMode = () => dispatchPrintMode({ type: "enter-print-mode" })
    const disablePrintMode = () => dispatchPrintMode({ type: "leave-print-mode" })

    updatePrintMode()
    printQuery.addEventListener("change", updatePrintMode)
    window.addEventListener("beforeprint", enablePrintMode)
    window.addEventListener("afterprint", disablePrintMode)
    window.addEventListener("focus", disablePrintMode)
    window.addEventListener("pageshow", disablePrintMode)

    return () => {
      printQuery.removeEventListener("change", updatePrintMode)
      window.removeEventListener("beforeprint", enablePrintMode)
      window.removeEventListener("afterprint", disablePrintMode)
      window.removeEventListener("focus", disablePrintMode)
      window.removeEventListener("pageshow", disablePrintMode)
    }
  }, [])

  return printModeState
}

const BlurFade: FC<BlurFadeProps> = ({
  blur = "6px",
  children,
  className,
  delay = 0,
  direction = "down",
  duration = 0.1,
  inView = false,
  inViewMargin = "-50px",
  motionProps,
  offset = 6,
  variant,
  ...props
}) => {
  const ref = useRef(null)
  const inViewResult = useInView(ref, { margin: inViewMargin, once: true })
  const { hasEnteredPrintMode, isPrintMode } = usePrintModeState()
  const forceVisible = isPrintMode || hasEnteredPrintMode
  const isInView = forceVisible || !inView || inViewResult

  const defaultVariants: Variants = {
    hidden: {
      [direction === "left" || direction === "right" ? "x" : "y"]:
        direction === "right" || direction === "down" ? -offset : offset,
      filter: `blur(${blur})`,
      opacity: 0,
    },
    visible: {
      [direction === "left" || direction === "right" ? "x" : "y"]: 0,
      filter: `blur(0px)`,
      opacity: 1,
    },
  }
  const combinedVariants = variant || defaultVariants
  return (
    <LazyMotion features={domAnimation}>
      <AnimatePresence>
        <HStack
          alignItems="flex-start"
          asChild
          flexGrow={1}
          gap={0}
          justifyContent="flex-start"
          w="full"
          {...props}
        >
          <m.div
            animate={isInView ? "visible" : "hidden"}
            className={className}
            data-blur-fade=""
            exit={forceVisible ? undefined : "hidden"}
            initial={forceVisible ? false : "hidden"}
            ref={ref}
            transition={{
              delay: forceVisible ? 0 : 0.04 + delay,
              duration: forceVisible ? 0 : duration,
              ease: "easeOut",
            }}
            variants={combinedVariants}
            {...motionProps}
          >
            {children}
          </m.div>
        </HStack>
      </AnimatePresence>
    </LazyMotion>
  )
}

export default BlurFade
